import {
  areaFromLegacySection,
  conquestsAreaQueryValue,
} from "./conquests-area";
import type { ConquestsArea } from "./types";

/** Chave canônica da área na query. Exportada em #245 para a rota responsiva
 * saber se a área já veio pedida, em vez de recalculá-la e engoli-la. */
export const CONQUESTS_QUERY_KEY = "conquistas";
const CANONICAL_AREA_VALUES = new Set([
  "agora",
  "ganhar",
  "competir",
  "perfil",
]);

export interface ConquestsUrlState {
  readonly activeArea: ConquestsArea;
  readonly open: boolean;
}

/** Read the human-facing Central URL while accepting pre-v3 overlay links.
 * @example `conquestsUrlState(new URLSearchParams("conquistas=competir"))` */
export function conquestsUrlState(params: URLSearchParams): ConquestsUrlState {
  const canonicalValues = params.getAll(CONQUESTS_QUERY_KEY);
  if (canonicalValues.length > 0) {
    return canonicalConquestsUrlState(canonicalValues);
  }
  return legacyConquestsUrlState(params);
}

/** Put one canonical Central destination in the current workspace URL.
 * @example `withConquestsAreaSearchParams(params, "compete")` */
export function withConquestsAreaSearchParams(
  current: URLSearchParams,
  area: ConquestsArea,
): URLSearchParams {
  const next = withoutConquestsSearchParams(current);
  next.set(CONQUESTS_QUERY_KEY, conquestsAreaQueryValue(area));
  return next;
}

/** Remove canonical and compatibility-owned Central location fields.
 * @example `withoutConquestsSearchParams(params)` */
export function withoutConquestsSearchParams(
  current: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete(CONQUESTS_QUERY_KEY);
  next.delete("overlay");
  next.delete("section");
  return next;
}

/** Replace compatibility-only query names without adding a history entry.
 * @example `normalizedConquestsSearchParams(new URLSearchParams("overlay=conquistas"))` */
export function normalizedConquestsSearchParams(
  current: URLSearchParams,
): URLSearchParams | null {
  const state = conquestsUrlState(current);
  if (!state.open || canonicalQueryIsClean(current, state.activeArea)) {
    return null;
  }
  return withConquestsAreaSearchParams(current, state.activeArea);
}

function canonicalQueryIsClean(
  current: URLSearchParams,
  area: ConquestsArea,
): boolean {
  const values = current.getAll(CONQUESTS_QUERY_KEY);
  return (
    values.length === 1 &&
    values[0] === conquestsAreaQueryValue(area) &&
    !current.has("overlay") &&
    !current.has("section")
  );
}

function canonicalConquestsUrlState(
  values: readonly string[],
): ConquestsUrlState {
  const value = values.length === 1 ? values[0] : undefined;
  const open = value !== undefined && CANONICAL_AREA_VALUES.has(value);
  return {
    activeArea: open ? areaFromLegacySection(value) : "now",
    open,
  };
}

function legacyConquestsUrlState(params: URLSearchParams): ConquestsUrlState {
  const overlays = params.getAll("overlay");
  const open = overlays.length === 1 && overlays[0] === "conquistas";
  return {
    activeArea: areaFromLegacySection(oneValue(params, "section")),
    open,
  };
}

function oneValue(params: URLSearchParams, key: string): string | null {
  const values = params.getAll(key);
  return values.length === 1 ? (values[0] ?? null) : null;
}
